/**
 * Class SimpleTabs manages a tabbed interface where users can switch between tabs to display associated content.
 * This class ensures that only the content related to the active tab is visible, simplifying tab management on any part of the webpage.
 *
 * How it works:
 * - Each tab and its corresponding content pane are linked via unique IDs.
 * - The class expects:
 *   1. A tab element identified by `data-simple-tab="some_id"` which, when clicked, will display its associated content pane(s).
 *   2. Content panes identified by `data-simple-tab-content="some_id"` which are initially hidden except for the default active content.
 * - When a tab is clicked, all content panes are hidden except for the ones associated with the clicked tab.
 * - The active tab can be marked with a CSS class 'active' for styling purposes.
 *
 * Usage:
 * - To initialize the tabs functionality, ensure all components are marked with appropriate data attributes and call `SimpleTabs.init()` after the document content has loaded.
 */
export default class SimpleTabs {
  constructor(tab) {
    this.tab = tab;
    this.allContents = document.querySelectorAll("[data-simple-tab-content]");
    this.allTabs = document.querySelectorAll("[data-simple-tab]");
    this.tab.addEventListener("click", (e) => {
      e.preventDefault();
      this.showTab();
    });
  }

  showTab() {
    const contentId = this.tab.getAttribute("data-simple-tab");
    const matchingContents = document.querySelectorAll(
      `[data-simple-tab-content="${contentId}"]`
    );
    const allSimilarTabs = document.querySelectorAll(
      `[data-simple-tab="${contentId}"]`
    );

    // Hide all contents and remove active class from all tabs
    this.allContents.forEach(
      (contentPane) => (contentPane.style.display = "none")
    );
    this.allTabs.forEach((tabPane) => tabPane.classList.remove("active"));

    // Show all the selected contents and mark all similar tabs as active
    matchingContents.forEach((content) => (content.style.display = "block"));
    allSimilarTabs.forEach((tab) => tab.classList.add("active"));
  }

  static init() {
    const tabs = document.querySelectorAll("[data-simple-tab]");
    tabs.forEach((tab) => new SimpleTabs(tab));
  }
}
